import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Loader2 } from 'lucide-react';

const SeaDeliveryOrderPrint = forwardRef(({ docketId, formData, companyData }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [loading, setLoading] = useState(!companyData);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (companyData) { setCompany(companyData); setLoading(false); return; }
    (async () => {
      try {
        const dr = await fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuth() });
        if (dr.ok) {
          const d = await dr.json();
          const cr = await fetch(`${backendUrl}/api/companies/${d.company_id}`, { headers: getAuth() });
          if (cr.ok) setCompany(await cr.json());
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [docketId, companyData]);

  useImperativeHandle(ref, () => ({ print: () => window.print() }));

  const fmtDate = (s) => {
    if (!s) return '';
    try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return s; }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
  );

  const companyName = company?.name || '';
  const companyLogo    = company?.logo_url;
  const companyAddress = [company?.address, company?.city, company?.state, company?.pincode].filter(Boolean).join(', ');
  const companyPhone   = company?.phone    || '';
  const companyEmail   = company?.email    || '';
  const companyWebsite = company?.website  || '';
  const companyGst     = company?.gst_number || '';
  const companyCin     = company?.cin_number || '';

  // Merge multi-container fields into comma-separated strings
  const containers = Array.isArray(formData.containers) ? formData.containers : [];
  const merge = (field) => containers.map(c => c[field] || '').filter(Boolean).join(',');
  const containerNos  = merge('container_no');
  const sizes         = merge('size');
  const sealNos       = merge('l_seal_no');
  const packages      = merge('no_of_packages');
  const grossWeights  = merge('gross_weight');

  // 2-column detail table rows
  const tableRows = [
    { label: 'MBL No. / Date',    value: [formData.mbl_no, fmtDate(formData.mbl_date)].filter(Boolean).join(' / ') },
    { label: 'HBL No. / Date',    value: [formData.hbl_no, fmtDate(formData.hbl_date)].filter(Boolean).join(' / ') },
    { label: 'Container No.',     value: containerNos },
    { label: 'Size',              value: sizes },
    { label: 'Seal No.',          value: sealNos },
    { label: 'No. of packages',   value: packages },
    { label: 'Gross Wt.',         value: grossWeights },
    { label: 'Vessel / Voyage',   value: formData.vessel_voyage || '' },
    { label: 'Consignee',         value: formData.consignee || '' },
    { label: 'Address',           value: formData.address || '' },
    { label: 'Port of Load',      value: formData.port_of_load || '' },
    { label: 'Port of Discharge', value: formData.port_of_discharge || '' },
    { label: 'Cargo Description', value: formData.cargo_description || '' },
  ];

  const td = {
    border: '1px solid #000',
    padding: '5px 8px',
    fontSize: '11px',
    verticalAlign: 'top',
  };
  const labelTd = { ...td, width: '35%', fontWeight: 'normal' };
  const valueTd = { ...td, width: '65%' };

  return (
    <>
      <div
        id="sea-do-print-content"
        style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.5', color: '#000', background: '#fff' }}
      >
        <div style={{ padding: '20mm 18mm', minHeight: 'calc(297mm - 40mm)', display: 'flex', flexDirection: 'column', position: 'relative' }}>

          {/* Watermark */}
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', pointerEvents: 'none', zIndex: 0,
          }}>
            <div style={{
              fontSize: '52px', fontWeight: '700', color: '#000', opacity: 0.04,
              transform: 'rotate(-30deg)', whiteSpace: 'nowrap', letterSpacing: '6px',
            }}>
              Execution of Commitments
            </div>
          </div>

          <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>

            {/* ── Header: Logo left, services right ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div>
                {companyLogo
                  ? <img src={companyLogo} alt={companyName} style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
                  : <div style={{ height: '64px', width: '80px', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#9ca3af', border: '1px solid #d1d5db' }}>LOGO</div>
                }
              </div>
              <div style={{ textAlign: 'right', fontSize: '11px' }}>
                <div style={{ fontWeight: '700' }}>Freight Forwarder</div>
                <div style={{ fontWeight: '700' }}>Custom Broker</div>
                <div style={{ fontWeight: '700' }}>Reefer Transportation</div>
                <div style={{ fontWeight: '700' }}>Logistics Solution</div>
                {companyWebsite && <div style={{ marginTop: '3px', color: '#2563eb' }}>{companyWebsite}</div>}
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '2px solid #000', marginBottom: '12px' }}></div>

            {/* Title */}
            <div style={{ textAlign: 'center', fontWeight: '700', fontSize: '15px', marginBottom: '18px' }}>
              NOC - DELIVERY ORDER
            </div>

            {/* Job No + Date / Valid Date */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px' }}>
              <div style={{ fontSize: '11px' }}>
                <span style={{ fontWeight: '600' }}>Job No.: </span>
                <span>{formData.job_no || ''}</span>
              </div>
              <div style={{ textAlign: 'right', fontSize: '11px' }}>
                <div><span style={{ fontWeight: '600' }}>Date: </span><span>{fmtDate(formData.release_date)}</span></div>
                <div style={{ marginTop: '3px' }}><span style={{ fontWeight: '600' }}>Valid Date: </span><span>{fmtDate(formData.valid_date)}</span></div>
              </div>
            </div>

            {/* To block */}
            <div style={{ marginBottom: '22px', fontSize: '11px' }}>
              <div>To,</div>
              <div style={{ fontWeight: '700', whiteSpace: 'pre-line' }}>{formData.to || ''}</div>
            </div>

            {/* Dear Sir / Madam */}
            <div style={{ fontWeight: '700', marginBottom: '4px', fontSize: '11px' }}>Dear Sir / Madam,</div>
            <div style={{ marginBottom: '14px', fontSize: '11px' }}>
              Sub : {formData.subject || 'NOC / Delivery order'}
            </div>

            {/* Main details table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
              <tbody>
                {tableRows.map(({ label, value }) => (
                  <tr key={label}>
                    <td style={labelTd}>{label}</td>
                    <td style={valueTd}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Request line */}
            <div style={{ marginBottom: '10px', fontSize: '11px' }}>
              We request you to kindly arrange to issue the DELIVERY ORDER in favor of
            </div>

            {/* Consignee / Broker table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
              <tbody>
                <tr>
                  <td style={labelTd}>Consignee Name</td>
                  <td style={valueTd}>{formData.consignee_name || ''}</td>
                </tr>
                <tr>
                  <td style={labelTd}>Custom broker Name</td>
                  <td style={valueTd}>{formData.custom_broker_name || ''}</td>
                </tr>
              </tbody>
            </table>

            {/* Legal text */}
            <div style={{ fontSize: '11px', marginBottom: '10px' }}>
              Subject to collection of the necessary charges from the receiver at your end.
            </div>
            <div style={{ fontSize: '11px', marginBottom: '10px' }}>
              We as the consignee, named on the MBL, hereby undertake to clear the shipment and pay to you as the carrier; all such cost that may become payable if the consignment remains un-cleared or abandoned.
            </div>
            <div style={{ fontSize: '11px', marginBottom: '18px' }}>
              We further indemnify you for all such costs that may arise out of damage to the container while in our / Receiver's procession. We agree to pay to you the differential payments as may become due in case the Receiver returns the container in the nominated CFS / Depot after the expiry of the validity date as mentioned on your DELIVERY ORDER.
            </div>

            {/* MBL statement */}
            <div style={{ fontSize: '11px', marginBottom: '24px' }}>
              Notwithstanding the foregoing and in addition there to our obligations under the above-mentioned MBL No. ------------------------------ are not affected and its terms &amp; conditions still apply.
            </div>

            {/* Signature — push to bottom */}
            <div style={{ marginTop: 'auto', textAlign: 'right', paddingTop: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '40px' }}>
                For, {companyName}
              </div>
              <div style={{ fontSize: '12px' }}>Authorized Signatory</div>
            </div>

            {/* ── Footer ── */}
            <div id="sea-do-footer" style={{ marginTop: '16px' }}>
              <div style={{ borderTop: '1px solid #000', marginBottom: '6px' }}></div>
              <div style={{ textAlign: 'center', fontSize: '9px', color: '#374151' }}>
                <div style={{ fontWeight: '700' }}>{companyName}</div>
                {companyAddress && <div>Regd. Office: {companyAddress}</div>}
                <div>
                  {companyPhone && <span>Phone: {companyPhone}</span>}
                  {companyPhone && companyEmail && <span> | </span>}
                  {companyEmail && <span>Email: {companyEmail}</span>}
                </div>
                <div>
                  {companyGst && <span>GSTIN: {companyGst}</span>}
                  {companyGst && companyCin && <span> | </span>}
                  {companyCin && <span>CIN/LLP: {companyCin}</span>}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          body * { visibility: hidden; }
          #sea-do-print-content, #sea-do-print-content * { visibility: visible; }
          #sea-do-print-content {
            position: absolute; left: 0; top: 0; width: 100%;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          #sea-do-print-content > div { flex: 1 !important; display: flex !important; flex-direction: column !important; }
          #sea-do-print-content > div > div { flex: 1 !important; display: flex !important; flex-direction: column !important; }
          #sea-do-footer { margin-top: auto !important; }
          table, td { border-color: #000 !important; }
        }
        @media screen {
          #sea-do-print-content { max-width: 210mm; margin: 0 auto; box-shadow: 0 0 12px rgba(0,0,0,0.12); }
        }
      `}</style>
    </>
  );
});

SeaDeliveryOrderPrint.displayName = 'SeaDeliveryOrderPrint';
export default SeaDeliveryOrderPrint;
